import { makeArticle, saintQuestionRelated } from "./learnFactory";

export const pageSaintTmsProtocol = makeArticle({
  slug: "saint-tms-protocol",
  title:
    "SAINT\u00ae Protocol: 50 Sessions in 5 Days, Explained | Neuro Wellness",
  description:
    "The SAINT\u00ae protocol is 50 sessions of fMRI-guided intermittent theta burst stimulation over 5 consecutive days. What it involves, who it's for, and where to get it.",
  canonical: "/learn/saint-tms-protocol",
  h1: "The SAINT\u00ae Protocol: 50 Sessions in 5 Days, Explained",
  breadcrumbLabel: "The SAINT® Protocol",
  shortAnswer:
    "SAINT® (Stanford Accelerated Intelligent Neuromodulation Therapy) is a five-day brain stimulation protocol for major depressive disorder. It delivers 50 sessions of intermittent theta burst stimulation (iTBS) over five consecutive days — 10 sessions a day, about 10 minutes each, with 50-minute breaks between them. An fMRI scan maps a personalized target in the left dorsolateral prefrontal cortex, and the targeting software is part of the FDA-cleared Magnus Neuromodulation System. Individual results may vary.",
  keyTakeaways: [
    "SAINT® is 50 sessions of intermittent theta burst stimulation over five consecutive days.",
    "Each day has 10 sessions of about 10 minutes, with 50-minute breaks between them.",
    "An fMRI scan identifies a personalized target in the left dorsolateral prefrontal cortex.",
    "The targeting software is part of the FDA-cleared Magnus Neuromodulation System (K220177).",
    "SAINT® is FDA-cleared for adults with major depressive disorder that hasn't improved enough on prior antidepressants.",
  ],
  sections: [
    {
      heading: "Quick answer: what the SAINT® protocol is",
      paragraphs: [
        "The SAINT® protocol — Stanford Accelerated Intelligent Neuromodulation Therapy — is an accelerated form of transcranial magnetic stimulation (TMS) developed at Stanford University and now delivered on the FDA-cleared Magnus Neuromodulation System. It compresses what is normally six to nine weeks of standard TMS into five consecutive days.",
        "In a SAINT® week, you have 50 sessions of intermittent theta burst stimulation (iTBS): 10 sessions a day, each about 10 minutes long, with roughly 50-minute breaks between sessions. Before the week begins, a functional MRI (fMRI) scan identifies a personalized target in the left dorsolateral prefrontal cortex (DLPFC), and neuronavigation keeps the coil on that exact target every session.",
        "SAINT® is FDA-cleared for adults with major depressive disorder (MDD) whose depression hasn't improved enough on prior antidepressant medication. Neuro Wellness TMS Centers of America (NWCOA) treats adults 22 and older. Individual results may vary.",
      ],
    },
    {
      heading: "How the SAINT® protocol works, step by step",
      paragraphs: [
        "The protocol has four parts: brain imaging, personalized targeting, the five-day treatment week, and follow-up. Here is what each involves.",
      ],
      list: [
        "Brain imaging: You have a structural MRI (a detailed 3D map of your brain's anatomy) and a resting-state functional MRI (fMRI), which measures how different brain regions communicate while you lie still. No injections or dyes are involved.",
        "Personalized targeting: Magnus Medical's software analyzes your fMRI to find a precise spot in the left DLPFC — specifically, the point whose activity is most strongly anticorrelated with the subgenual anterior cingulate cortex (sgACC), a region involved in mood regulation.",
        "The treatment week: Over five consecutive days, you receive 10 sessions of iTBS per day. Each session is about 10 minutes of stimulation, followed by a 50-minute break. That adds up to about 50 sessions and roughly 90,000 magnetic pulses total.",
        "Follow-up: After the week, your clinician tracks your mood scores and coordinates any next steps with you and, if you have one, your referring provider.",
      ],
    },
    {
      heading: "How long the SAINT® protocol takes",
      paragraphs: [
        "The full SAINT® protocol is five consecutive days. Each treatment day involves 10 sessions spaced about 50 minutes apart, so a full day in the clinic runs several hours. You are awake throughout and can usually drive yourself to and from the clinic.",
        "Compared with standard TMS — which typically runs 36 sessions over six to nine weeks — SAINT® compresses the entire course into one week. That is the main practical difference: far fewer trips to the clinic, and a much shorter window before you know whether the treatment is helping.",
      ],
    },
    {
      heading: "Why the protocol and the system can't be separated",
      paragraphs: [
        "The SAINT® protocol and the Magnus Neuromodulation System are not separate things you can mix and match. The FDA clearance (510(k) K220177, September 2022) covers the protocol together with the Magnus system's targeting software. The personalized fMRI-guided targeting is part of what the FDA cleared.",
        "That matters because some clinics offer 'SAINT-style' or 'fMRI-guided accelerated TMS' protocols that copy the schedule — 10 sessions a day, five days, iTBS — but do not use the FDA-cleared SAINT targeting system. They can copy the calendar, but the FDA-cleared targeting software and the outcome data from the SAINT trials do not transfer to those protocols. If a clinic cannot confirm it uses the Magnus SAINT Neuromodulation System, it is not delivering SAINT®.",
      ],
    },
    {
      heading: "Who the SAINT® protocol is for (and who it isn't)",
      paragraphs: [
        "SAINT® is FDA-cleared for adults with major depressive disorder whose depression hasn't improved enough on prior antidepressant medication. NWCOA treats adults 22 and older.",
        "SAINT® is not cleared for anxiety, OCD, PTSD, bipolar disorder, concussion, or any condition other than MDD, and it is not for minors. It is also contraindicated for people with non-removable magnetic-sensitive metal in or near the head, certain implanted devices, or increased intracranial pressure. A consultation confirms whether SAINT® fits your history.",
        "Eligibility is clinical — it depends on your diagnosis, your treatment history, and your medical screening, not on where you live. [[Check whether SAINT® is a fit|/am-i-a-candidate-screener]].",
      ],
    },
    {
      heading: "Where the SAINT® protocol is offered",
      paragraphs: [
        "SAINT® is delivered only on the Magnus Neuromodulation System by clinics verified by Magnus Medical. NWCOA is a Verified SAINT® Provider in Coral Springs, Florida. To find verified providers in other states, use the [[Magnus provider locator|https://www.magnusmedical.com]] and our [[SAINT® TMS locations hub|/learn/saint-tms-locations]].",
        "Because SAINT® takes only one week, many patients travel to a verified provider. If you are coming from out of state, [[see our travel guide|/learn/travel-for-saint-tms]] for airports, lodging, and how the week works for travelers.",
      ],
      links: [
        { to: "/saint", text: "Learn more about SAINT®" },
        { to: "/learn/saint-tms-cost", text: "See SAINT® cost and insurance" },
        { to: "/check-insurance-options", text: "Check my insurance" },
      ],
    },
    {
      heading: "SAINT® protocol vs. standard TMS",
      paragraphs: [
        "The table below compares the SAINT® protocol with standard TMS so you can see the differences at a glance.",
      ],
      table: {
        headers: ["Protocol element", "SAINT®", "Standard TMS"],
        rows: [
          [
            "Pulse pattern",
            "Intermittent theta burst (iTBS)",
            "10 Hz repetitive TMS",
          ],
          ["Sessions per day", "10", "1"],
          ["Days", "5 consecutive days", "6–9 weeks"],
          ["Total sessions", "50", "~36"],
          [
            "Targeting",
            "fMRI-guided, personalized per person",
            "Standard anatomical landmark",
          ],
          ["Device", "Magnus Neuromodulation System", "Various TMS devices"],
          ["FDA status", "Cleared (K220177) for MDD", "Cleared for MDD"],
        ],
      },
    },
    {
      heading: "What to do next",
      paragraphs: [
        "Understanding the protocol is the easy part. Knowing whether it fits your history is what a consultation is for. NWCOA is a Verified SAINT® Provider in Coral Springs, Florida, and we'll walk you through your scan, your treatment week, and what to expect — step by step.",
        "You can [[start the free candidacy screener|/am-i-a-candidate-screener]], [[check your insurance benefits|/check-insurance-options]], or [[learn more about SAINT®|/saint]].",
      ],
    },
  ],
  faqs: [
    {
      question: "What is the SAINT protocol?",
      answer:
        "SAINT® (Stanford Accelerated Intelligent Neuromodulation Therapy) is a five-day TMS protocol: 50 sessions of intermittent theta burst stimulation over five consecutive days, with 10 sessions per day. An fMRI scan personalizes the target in the left dorsolateral prefrontal cortex. It is delivered on the FDA-cleared Magnus Neuromodulation System.",
    },
    {
      question: "How long does the SAINT protocol take?",
      answer:
        "Five consecutive days. Each day has 10 sessions of about 10 minutes each, with roughly 50-minute breaks between sessions, so a full treatment day runs several hours. The entire course is 50 sessions — compared with about 36 sessions over six to nine weeks for standard TMS.",
    },
    {
      question: "Is the SAINT protocol FDA-approved?",
      answer:
        "SAINT® is FDA-cleared (510(k) K220177, September 2022) for adults with major depressive disorder whose depression hasn't improved enough on prior antidepressants. The clearance covers the protocol together with the Magnus Neuromodulation System's targeting software. Individual results may vary.",
    },
    {
      question: "Can any clinic offer the SAINT protocol?",
      answer:
        "No. SAINT® is delivered only on the Magnus Neuromodulation System by clinics verified by Magnus Medical. A clinic can copy the schedule — 10 sessions a day for five days — but without the FDA-cleared targeting software it is not delivering SAINT®, and the SAINT trial outcomes do not transfer to it. Ask whether the clinic uses the Magnus system.",
    },
    {
      question: "Is the 'Saint George protocol' the same thing?",
      answer:
        "No. 'Saint George protocol' is not a recognized or FDA-cleared protocol. SAINT® (Stanford Accelerated Intelligent Neuromodulation Therapy) is the protocol developed at Stanford and delivered on the Magnus Neuromodulation System. If a clinic references a different name, confirm it uses the Magnus SAINT system.",
    },
    {
      question: "Does the SAINT protocol require anesthesia?",
      answer:
        "No. SAINT® is non-invasive and you are awake throughout. There are no injections or sedation, and you can typically drive yourself to and from the clinic.",
    },
  ],
  sources: [
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): A Double-Blind Randomized Controlled Trial. Am J Psychiatry. 2022;179(2):132–141.",
      url: "https://pubmed.ncbi.nlm.nih.gov/35624717/",
    },
    {
      citation:
        "Cole EJ, et al. Stanford Accelerated Intelligent Neuromodulation Therapy for Treatment-Resistant Depression. Am J Psychiatry. 2020;177:716–726.",
      url: "https://pubmed.ncbi.nlm.nih.gov/32552917/",
    },
    {
      citation:
        "U.S. Food and Drug Administration. 510(k) Premarket Notification — Magnus Neuromodulation System with SAINT Technology (K220177, September 2022).",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm",
    },
  ],
  relatedLinks: saintQuestionRelated,
});
